import {
  encodeHeaderFileName,
  FILE_NAME_ENCODING_HEADER,
  FILE_NAME_ENCODING_PCT,
} from "@fast-quotation/contracts";

export type ApiErrorPayload = { error: { code: string; message: string; details?: unknown } };

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: "same-origin",
    headers: {
      "content-type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  const text = await response.text();
  const payload = text ? JSON.parse(text) : {};
  // 401 = 没登录 / 会话过期或被吊销：通知全局切回登录页，而不是把错误文字丢在页面上
  if (response.status === 401) {
    window.dispatchEvent(new CustomEvent("fq:unauthorized"));
  }
  if (!response.ok) {
    const message = (payload as ApiErrorPayload)?.error?.message ?? `请求失败 (${response.status})`;
    throw new Error(message);
  }
  return payload as T;
}

export const api = {
  login: (email: string, password: string) =>
    request<{ actor: { email: string; role: string; departmentId: string } }>("/api/v1/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<{ ok: true }>("/api/v1/auth/logout", { method: "POST" }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true }>("/api/v1/auth/password", {
      method: "POST",
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    }),
  setMemberPassword: (memberId: string, password: string) =>
    request<{ ok: true }>(`/api/v1/members/${memberId}/password`, {
      method: "POST",
      body: JSON.stringify({ password }),
    }),
  health: () => request<{ ok: boolean; calcMethods: string[]; environment: string }>("/api/v1/health"),
  me: () => request<{ actor: { email: string; role: string; departmentId: string } }>("/api/v1/me"),
  categories: () =>
    request<{ categories: Array<{ id: string; code: string; name: string; parent_id: string | null }> }>(
      "/api/v1/categories",
    ),
  catalogItems: (params: { category?: string; q?: string; includeInactive?: boolean } = {}) => {
    const search = new URLSearchParams();
    if (params.category) search.set("category", params.category);
    if (params.q) search.set("q", params.q);
    // 只有管理员能带上这个：组件目录里"显示已停用"，方便停错了再放回来
    if (params.includeInactive) search.set("include_inactive", "1");
    const suffix = search.size > 0 ? `?${search.toString()}` : "";
    return request<{ items: CatalogItemRow[] }>(`/api/v1/catalog-items${suffix}`);
  },
  cabinetTypes: () =>
    request<{
      cabinetTypes: Array<{
        code: string;
        name: string;
        /** 英文名（客户版报价单用） */
        name_en: string | null;
        default_height_mm: number | null;
        /** 画框时预填的默认深度（mm）；null = 不预填 */
        default_depth_mm: number | null;
        slots: unknown[];
      }>;
    }>("/api/v1/cabinet-types"),
  /** 批量替换：整单 / 某柜型 / 指定条目，把某个槽位的材料整批换掉并重算 */
  bulkReplace: (
    planId: string,
    body: {
      scope: { type: "plan" } | { type: "cabinet_type"; code: string } | { type: "items"; itemIds: string[] };
      slot: string;
      catalog_item_id: string;
      from_catalog_code?: string;
    },
  ) =>
    request<{
      affectedItems: number;
      changedRows: number;
      skipped: Array<{ label: string; reason: string }>;
      totals: { costCents: number; priceCents: number; pendingItems: number };
    }>(`/api/v1/plans/${planId}/bulk-replace`, { method: "POST", body: JSON.stringify(body) }),
  /** 批量加项：把按件项目（纸箱 / 木箱 / 组装…）加到整单 / 某柜型 / 指定条目的每组柜子上 */
  bulkAdd: (
    planId: string,
    body: {
      scope: { type: "plan" } | { type: "cabinet_type"; code: string } | { type: "items"; itemIds: string[] };
      slot: string;
      catalog_item_id: string;
      quantity_per_item?: number;
      skip_existing?: boolean;
    },
  ) =>
    request<{
      addedItems: number;
      addedRows: number;
      skipped: Array<{ label: string; reason: string }>;
      totals: { costCents: number; priceCents: number; pendingItems: number };
    }>(`/api/v1/plans/${planId}/bulk-add`, { method: "POST", body: JSON.stringify(body) }),
  uploadDrawing: async (planId: string, file: File, pageCount: number) => {
    const response = await fetch(`/api/v1/plans/${planId}/drawings`, {
      method: "POST",
      headers: {
        // 中文文件名不能直接进请求头（fetch 会抛 non ISO-8859-1），先 percent-encode
        "x-file-name": encodeHeaderFileName(file.name),
        [FILE_NAME_ENCODING_HEADER]: FILE_NAME_ENCODING_PCT,
        "x-page-count": String(pageCount),
      },
      body: file,
    });
    const payload = await response.json();
    if (!response.ok)
      throw new Error((payload as { error?: { message?: string } })?.error?.message ?? "上传失败");
    return payload as { drawing: DrawingRow; deduplicated?: boolean };
  },
  drawing: (drawingId: string) =>
    request<{
      drawing: DrawingRow;
      pages: Array<{
        page_index: number;
        width: number;
        height: number;
        rotation: number;
        ruler_x1: number | null;
        ruler_y1: number | null;
        ruler_x2: number | null;
        ruler_y2: number | null;
        ruler_length_mm: number | null;
      }>;
    }>(`/api/v1/drawings/${drawingId}`),
  saveRuler: (
    drawingId: string,
    pageIndex: number,
    ruler: { x1: number; y1: number; x2: number; y2: number; lengthMm: number },
  ) =>
    request<{ ok: true }>(`/api/v1/drawings/${drawingId}/pages/${pageIndex}/ruler`, {
      method: "PUT",
      body: JSON.stringify(ruler),
    }),
  clearRuler: (drawingId: string, pageIndex: number) =>
    request<{ ok: true }>(`/api/v1/drawings/${drawingId}/pages/${pageIndex}/ruler`, { method: "DELETE" }),
  reportPages: (
    drawingId: string,
    pages: Array<{ page_index: number; width: number; height: number; rotation: number }>,
  ) =>
    request<{ ok: true }>(`/api/v1/drawings/${drawingId}/pages`, {
      method: "PUT",
      body: JSON.stringify({ pages }),
    }),
  plans: () => request<{ plans: PlanRow[] }>("/api/v1/plans"),
  createPlan: (body: { name: string; department_id: string; parent_id?: string | null }) =>
    request<{ plan: PlanRow }>("/api/v1/plans", { method: "POST", body: JSON.stringify(body) }),
  /** 一键复制方案：整份深拷贝（图纸/标尺/条目/组件快照/子方案），副本从 draft 起步。 */
  duplicatePlan: (planId: string, body: { name?: string } = {}) =>
    request<{
      plan: PlanRow;
      copied: { plans: number; drawings: number; pages: number; items: number; components: number };
    }>(`/api/v1/plans/${planId}/duplicate`, { method: "POST", body: JSON.stringify(body) }),
  /** 报价设置：客户币种 / 汇率 / 加价系数 / 整单调整（改完服务端自动重算全单） */
  updatePlan: (
    planId: string,
    body: {
      name?: string;
      currency?: string;
      exchange_rate?: number;
      markup_factor?: number;
      global_adjustment_percent?: number;
      include_floor_plan?: boolean;
      /** 付款方式：deposit_50_50（50% 定金）/ deposit_5000_70（5000 元定金 + 图纸确认后 70%） */
      payment_terms?: string;
      /** 整装出货：开了按货值加收 7% 打包组装费 */
      assembled_shipment?: boolean;
    },
  ) =>
    request<{ plan: PlanRow; totals: { costCents: number; priceCents: number; pendingItems: number } }>(
      `/api/v1/plans/${planId}`,
      { method: "PATCH", body: JSON.stringify(body) },
    ),
  departments: () =>
    request<{
      departments: Array<{ id: string; name: string; code: string; status: string; member_count?: number }>;
    }>("/api/v1/departments"),
  createDepartment: (body: { name: string; code?: string }) =>
    request<{ department: { id: string } }>("/api/v1/departments", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateDepartment: (id: string, body: { name?: string; status?: string }) =>
    request<{ department: unknown }>(`/api/v1/departments/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteDepartment: (id: string) => request<{ ok: true }>(`/api/v1/departments/${id}`, { method: "DELETE" }),
  members: () =>
    request<{
      members: Array<{
        id: string;
        department_id: string;
        department_name: string;
        email: string;
        display_name: string | null;
        role: string;
        status: string;
      }>;
    }>("/api/v1/members"),
  createMember: (body: {
    department_id: string;
    email: string;
    display_name?: string | null;
    role: string;
  }) =>
    request<{ member: { id: string } }>("/api/v1/members", { method: "POST", body: JSON.stringify(body) }),
  updateMember: (
    id: string,
    body: { role?: string; status?: string; department_id?: string; display_name?: string },
  ) => request<{ member: unknown }>(`/api/v1/members/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteMember: (id: string) => request<{ ok: true }>(`/api/v1/members/${id}`, { method: "DELETE" }),
  plan: (planId: string) =>
    request<{
      plan: PlanRow;
      items: PlanItemRow[];
      components: PlanItemComponentRow[];
      drawings: DrawingRow[];
      children: PlanRow[];
    }>(`/api/v1/plans/${planId}`),
  createItem: (planId: string, body: Record<string, unknown>) =>
    request<{ item: PlanItemRow }>(`/api/v1/plans/${planId}/items`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateItem: (itemId: string, body: Record<string, unknown>) =>
    request<{ item: PlanItemRow }>(`/api/v1/items/${itemId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  /** 条目排序：提交拖拽后的完整次序（数组下标 = 新顺序），编号不变。 */
  reorderItems: (planId: string, itemIds: string[]) =>
    request<{ items: PlanItemRow[] }>(`/api/v1/plans/${planId}/items/reorder`, {
      method: "POST",
      body: JSON.stringify({ item_ids: itemIds }),
    }),
  deleteItem: (itemId: string) => request<{ ok: true }>(`/api/v1/items/${itemId}`, { method: "DELETE" }),
  addComponent: (itemId: string, body: { slot: string; catalog_item_id: string }) =>
    request<{ component: PlanItemComponentRow }>(`/api/v1/items/${itemId}/components`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  deleteComponent: (itemId: string, componentId: string) =>
    request<{ ok: true }>(`/api/v1/items/${itemId}/components/${componentId}`, { method: "DELETE" }),
  updateComponent: (
    itemId: string,
    componentId: string,
    body: { params?: Record<string, unknown>; sort_order?: number; catalog_item_id?: string },
  ) =>
    request<{ component: PlanItemComponentRow }>(`/api/v1/items/${itemId}/components/${componentId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  /** 画完框立刻套柜型 + 算价，右侧马上出明细。 */
  recompute: (itemId: string) =>
    request<{
      item: PlanItemRow;
      components: PlanItemComponentRow[];
      totals: { costCents: number; priceCents: number; pendingItems: number };
    }>(`/api/v1/items/${itemId}/recompute`, { method: "POST", body: JSON.stringify({}) }),
  createCatalogItem: (body: Record<string, unknown>) =>
    request<{ item: CatalogItemRow }>("/api/v1/catalog-items", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateCatalogItem: (id: string, body: Record<string, unknown>) =>
    request<{ item: CatalogItemRow }>(`/api/v1/catalog-items/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  generate: (planId: string, mode: "fill_missing" | "reset_all" = "fill_missing") =>
    request<{
      createdComponents: number;
      totals: { costCents: number; priceCents: number; pendingItems: number };
    }>(`/api/v1/plans/${planId}/generate`, { method: "POST", body: JSON.stringify({ mode }) }),
  /** 客户视图：服务端已经剥掉内部成本，前端只负责排版和浏览器打印。 */
  print: (planId: string) =>
    request<{
      plan: {
        id: string;
        name: string;
        code: string | null;
        currency: string;
        status: string;
        totalPriceCents: number;
        /** 货值（条目报价合计）与整装出货附加费，报价单上分两行印 */
        goodsCents: number;
        assemblySurchargeCents: number;
        assembledShipment: boolean;
        /** 付款方式 code：deposit_50_50 / deposit_5000_70 */
        paymentTerms: string;
        /** 是否在报价单里附平面图 */
        includeFloorPlan: boolean;
      };
      items: Array<{
        id: string;
        label: string;
        room: string | null;
        cabinet_type_code: string;
        width_mm: number | null;
        height_mm: number | null;
        depth_mm: number | null;
        quantity: number;
        price_cents: number;
        size_status: string;
        /**
         * 这个条目自己的含料明细（柜体/门板/见光板/五金/台面 + 含不含灯带）。
         * 名称来自组件快照，不含单价、成本、params。
         */
        materials: {
          groups: Array<{
            key: "cabinet" | "door" | "panel" | "countertop" | "hardware" | "accessory";
            names: Array<{ zh: string; en: string }>;
          }>;
          countertopIncluded: boolean;
          ledIncluded: boolean;
          ledNames: Array<{ zh: string; en: string }>;
        };
      }>;
      /** 包装明细（纸箱 / 木箱…）：数量与已含在柜体价格里的金额，不含成本与系数 */
      packaging: Array<{
        code: string;
        name: string;
        name_en: string | null;
        unit: string;
        quantity: number;
        groups: number;
        price_cents: number;
      }>;
      includeInternalCost: boolean;
    }>(`/api/v1/plans/${planId}/print`),
};

export type PlanRow = {
  id: string;
  parent_id: string | null;
  department_id: string;
  name: string;
  code: string | null;
  status: string;
  cost_currency: string;
  currency: string;
  exchange_rate: number;
  markup_factor: number;
  global_adjustment_percent: number;
  /** 付款方式（报价单上照它出条款） */
  payment_terms: string;
  /** 整装出货：按货值加收 7% 打包组装费（0/1） */
  assembled_shipment: number;
  /** 报价单是否附平面图（0/1，效果图不适合进报价单时可关掉） */
  include_floor_plan: number;
  total_cost_cents: number;
  total_price_cents: number;
  item_count?: number;
};

export type PlanItemRow = {
  id: string;
  plan_id: string;
  drawing_id: string | null;
  page_index: number;
  box_x: number;
  box_y: number;
  box_w: number;
  box_h: number;
  cabinet_type_code: string;
  label: string;
  room: string | null;
  note: string | null;
  width_mm: number | null;
  height_mm: number | null;
  depth_mm: number | null;
  doors: number;
  drawers: number;
  shelves: number;
  quantity: number;
  /** 框体旋转角度（度） */
  rotation_deg: number;
  size_status: string;
  cost_cents: number;
  price_cents: number;
  sort_order: number;
};

export type PlanItemComponentRow = {
  id: string;
  item_id: string;
  slot: string;
  catalog_code: string;
  name_snapshot: string;
  calc_method: string;
  params: string;
  unit: string;
  unit_price_cents: number;
  quantity: number;
  amount_cents: number;
  state: string;
  notes: string | null;
};

export type CatalogItemRow = {
  id: string;
  code: string;
  name: string;
  /** 英文名：客户报价单上的材料名就用它（缺了会退回中文名，英文单子会中英混排） */
  name_en: string | null;
  calc_method: string;
  unit: string;
  unit_price_cents: number;
  category_code: string;
  category_name: string;
  params: string;
  spec: string | null;
  active: number;
  /** 归属部门（品牌）：null = 全公司共享 */
  department_id: string | null;
};

export type DrawingRow = {
  id: string;
  plan_id: string;
  file_key: string;
  file_name: string;
  mime_type?: string;
  sha256: string;
  page_count: number;
};
